export enum MenuIndex {
  BodPost,
  ManagementPost,
  FadPost,
  SupportingPost,
  HrgaPost,
  MarketingPost,
  ProductionPost,
  FssPost,
  GeneralPost,
  StorePost,
  ExclusivePost,
  LocPost,
  CommonPost,
  Album,
  User
}

interface IPermission {
  view: boolean;
  modify: boolean;
}

export interface IMenu {
  index: MenuIndex;
  name: string;
  shortName: string;
  url: string;
  permission: IPermission;
}

interface IMenuGroup {
  name: string;
  icon: string;
  menus: IMenu[];
}

export const newPermissions = () =>
  "00,00,00,00,00,00,00,00,10,00,00,00,00,00,00";

const menuDetails: [string, string, string, string, string][] = [];

// ---------------------------------------------- add menu
const addDetail = (
  index: MenuIndex,
  name: string,
  shortName: string,
  collection: string,
  reducer: string,
  url: string
): void => {
  menuDetails[index] = [name, shortName, collection, reducer, url];
};

addDetail(
  MenuIndex.BodPost,
  "BOD posts",
  "BOD",
  "bodPosts",
  "bodPost",
  "bod-posts"
);
addDetail(
  MenuIndex.ManagementPost,
  "Management posts",
  "Management",
  "managementPosts",
  "managementPost",
  "management-posts"
);
addDetail(
  MenuIndex.FadPost,
  "FAD posts",
  "FAD",
  "fadPosts",
  "fadPost",
  "fad-posts"
);
addDetail(
  MenuIndex.SupportingPost,
  "Supporting division posts",
  "Supporting Div",
  "supportingPosts",
  "supportingPost",
  "supporting-posts"
);
addDetail(
  MenuIndex.HrgaPost,
  "HR GA posts",
  "HR GA",
  "hrgaPosts",
  "hrgaPost",
  "hrga-posts"
);
addDetail(
  MenuIndex.MarketingPost,
  "Marketing posts",
  "Marketing",
  "marketingPosts",
  "marketingPost",
  "marketing-posts"
);
addDetail(
  MenuIndex.ProductionPost,
  "Production posts",
  "Production",
  "productionPosts",
  "productionPost",
  "production-posts"
);
addDetail(
  MenuIndex.FssPost,
  "FSS posts",
  "FSS",
  "fssPosts",
  "fssPost",
  "fss-posts"
);
addDetail(
  MenuIndex.GeneralPost,
  "General posts",
  "General",
  "generalPosts",
  "generalPost",
  "general-posts"
);
addDetail(
  MenuIndex.StorePost,
  "Store posts",
  "Store",
  "storePosts",
  "storePost",
  "store-posts"
);
addDetail(
  MenuIndex.ExclusivePost,
  "Exclusive posts",
  "Exclusive",
  "exclusivePosts",
  "exclusivePost",
  "exclusive-posts"
);
addDetail(
  MenuIndex.LocPost,
  "LOC posts",
  "LOC",
  "locPosts",
  "locPost",
  "loc-posts"
);
addDetail(
  MenuIndex.CommonPost,
  "Common posts",
  "Common",
  "commonPosts",
  "commonPost",
  "common-posts"
);
addDetail(MenuIndex.Album, "Album", "Album", "albums", "album", "album");
addDetail(
  MenuIndex.User,
  "Manage User",
  "Manage User",
  "users",
  "user",
  "user"
);

// ---------------------------------------------- indexes
const menuIndexes = [
  MenuIndex.BodPost,
  MenuIndex.ManagementPost,
  MenuIndex.FadPost,
  MenuIndex.SupportingPost,
  MenuIndex.HrgaPost,
  MenuIndex.MarketingPost,
  MenuIndex.ProductionPost,
  MenuIndex.FssPost,
  MenuIndex.GeneralPost,
  MenuIndex.StorePost,
  MenuIndex.ExclusivePost,
  MenuIndex.LocPost,
  MenuIndex.CommonPost,
  MenuIndex.Album,
  MenuIndex.User
];

// ---------------------------------------------- grouped indexes
const groupedIndexes = [
  {
    name: "Division posts",
    icon: "chat",
    menuIndexes: [
      MenuIndex.BodPost,
      MenuIndex.ManagementPost,
      MenuIndex.FadPost,
      MenuIndex.SupportingPost,
      MenuIndex.HrgaPost,
      MenuIndex.MarketingPost,
      MenuIndex.ProductionPost,
      MenuIndex.FssPost
    ]
  },
  {
    name: "Other posts",
    icon: "chat_bubble",
    menuIndexes: [
      MenuIndex.GeneralPost,
      MenuIndex.StorePost,
      MenuIndex.ExclusivePost,
      MenuIndex.LocPost,
      MenuIndex.CommonPost
    ]
  },
  {
    name: "Album",
    icon: "photo_library",
    menuIndexes: [MenuIndex.Album]
  },
  {
    name: "User",
    icon: "people",
    menuIndexes: [MenuIndex.User]
  }
];

// ---------------------------------------------- helper class
class PermissionHelper {
  static getName = (index: MenuIndex): string => menuDetails[index][0];

  static getShortName = (index: MenuIndex): string => menuDetails[index][1];

  static getCollection = (index: MenuIndex): string => menuDetails[index][2];

  static getReducer = (index: MenuIndex): string => menuDetails[index][3];

  static getUrl = (index: MenuIndex): string => menuDetails[index][4];

  static getView = (varchar: string | undefined, index: MenuIndex): boolean =>
    varchar
      ? parseInt(varchar.split(",")[index][0])
        ? true
        : false
      : parseInt(newPermissions().split(",")[index][0])
      ? true
      : false;

  static getModify = (varchar: string | undefined, index: MenuIndex): boolean =>
    varchar
      ? parseInt(varchar.split(",")[index][1])
        ? true
        : false
      : parseInt(newPermissions().split(",")[index][1])
      ? true
      : false;

  static toMenus = (varchar: string | undefined): IMenu[] =>
    menuIndexes.map(menuIndex => ({
      index: menuIndex,
      name: PermissionHelper.getName(menuIndex),
      shortName: PermissionHelper.getShortName(menuIndex),
      url: PermissionHelper.getUrl(menuIndex),
      permission: {
        view: PermissionHelper.getView(varchar, menuIndex) ? true : false,
        modify: PermissionHelper.getModify(varchar, menuIndex) ? true : false
      }
    }));

  static toMenuGroups = (varchar: string | undefined): IMenuGroup[] =>
    groupedIndexes.map(group => ({
      name: group.name,
      icon: group.icon,
      menus: group.menuIndexes.map(menuIndex => ({
        index: menuIndex,
        name: PermissionHelper.getName(menuIndex),
        shortName: PermissionHelper.getShortName(menuIndex),
        url: PermissionHelper.getUrl(menuIndex),
        permission: {
          view: PermissionHelper.getView(varchar, menuIndex) ? true : false,
          modify: PermissionHelper.getModify(varchar, menuIndex) ? true : false
        }
      }))
    }));

  static togglePermission = (
    permissions: string,
    index: MenuIndex,
    type: number
  ): string => {
    const toggled = type
      ? permissions.split(",")[index] === "11"
        ? "10"
        : "11"
      : permissions.split(",")[index] === "00"
      ? "10"
      : "00";
    const splitted = permissions.split(",");

    splitted.splice(index, 1, toggled).toString();

    return splitted.toString();
  };
}

export default PermissionHelper;
